import { useState, useEffect, useRef } from "react";
import { IoIosArrowDropleftCircle, IoIosArrowDroprightCircle } from "react-icons/io";

const ResizablePanel = ({ minWidth = 200, leftSide, rightSide }) => {
  const [leftWidth, setLeftWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const prevLeftWidthRef = useRef(0);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const handleWindowResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (!width) return;

      setContainerWidth(width);

      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const initialWidth = width / 2;
        setLeftWidth(initialWidth);
        prevLeftWidthRef.current = initialWidth;
      } else if (!isCollapsed) {
        setLeftWidth((prev) => Math.min(prev, Math.max(width - minWidth, minWidth)));
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minWidth]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const newLeftWidth = e.clientX - containerRect.left;
        if (
          newLeftWidth >= minWidth &&
          newLeftWidth <= containerWidth - minWidth
        ) {
          setLeftWidth(newLeftWidth);
          prevLeftWidthRef.current = newLeftWidth;
        }
      }
    };

    const handleMouseUp = () => {
      if (isResizing) setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, minWidth, containerWidth]);

  const handleCollapseToggle = () => {
    if (isCollapsed) {
      setLeftWidth(prevLeftWidthRef.current);
    } else {
      prevLeftWidthRef.current = leftWidth;
      setLeftWidth(0);
    }
    setIsCollapsed(!isCollapsed);
  };

  const isDesktop = windowWidth >= 991;

  return (
    <div
      ref={containerRef}
      className="flex w-full overflow-hidden "
      style={{ height: "80vh", flexDirection: isDesktop ? "row" : "column" }}
    >
      {/* Left Panel */}
      <div
        className="bg-gray-100 min-h-44 border-none border-gray-300"
        style={{
          width: isDesktop ? `${leftWidth}px` : "100%",
          height: isDesktop ? "100%" : "70vh",
          marginBottom: isDesktop ? 0 : "16px",
          overflow: "hidden",
          flexShrink: isDesktop ? 0 : 1,
          display: isDesktop && leftWidth === 0 ? "none" : "block",
        }}
      >
        {/* Left Content */}
        <div className="h-full">{leftSide}</div>
      </div>

      {/* Resize Handle */}
      {isDesktop && !isCollapsed && (
        <div
          onMouseDown={() => setIsResizing(true)}
          className="z-10 w-4 bg-canvas-primary/20 hover:bg-canvas-primary/30 cursor-col-resize flex flex-row items-center justify-center gap-[2px] relative"
          style={{ height: "100%" }}
        > <IoIosArrowDropleftCircle onClick={handleCollapseToggle} size={35} className="text-canvas-primary absolute right-auto left-auto top-[-3px] cursor-pointer bg-white rounded-full shadow-lg" />
          <span className="w-[2px] h-6 bg-canvas-primary rounded-full"></span>
          <span className="w-[2px] h-6 bg-canvas-primary rounded-full"></span>
          {/* <span className="w-[2px] h-6 bg-gray-700 rounded-full"></span> */}
        </div>
      )}

      {/* Expand Button (when collapsed) */}
      {isDesktop && isCollapsed && (
        <div className="flex items-start justify-start z-10 relative">
          <IoIosArrowDroprightCircle onClick={handleCollapseToggle} size={35} className="text-canvas-primary absolute cursor-pointer bg-white rounded-full shadow-lg top-[-2px] left-[-3px]" title="Click to Show Map" />
        </div>
      )}

      {/* Right Panel */}
      <div
        className="bg-white flex-grow flex flex-col"
        style={{
          width: isDesktop ? "auto" : "100%",
          height: isDesktop ? "100%" : "calc(100vh - 260px)",
          overflow: "hidden",
        }}
      >
        {rightSide}
      </div>
    </div>
  );
};

export default ResizablePanel;
